import React from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { formatAmount, formatDate } from './invoiceHelpers';

// Payments Tab Component
export const PaymentsTab = ({
  invoice,
  onCapturePayment,
  onEditPayment,
  onDeletePayment
}) => {
  return (
    <div className="py-4">
      {invoice.status === "raised" && onCapturePayment && (
        <div className="text-center mb-6">
          <p className="text-sm text-gray-400 mb-4">
            Note: You can capture payments only for raised invoices.
          </p>
          <button
            onClick={onCapturePayment}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            +Capture Payment
          </button>
        </div>
      )}

      {/* Payment List */}
      {invoice.payments && invoice.payments.length > 0 ? (
        <div className="space-y-4">
          <h4 className="text-white font-semibold mb-4">Payment History</h4>
          {invoice.payments.map((payment) => (
            <div key={payment.id} className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Amount</p>
                    <p className="text-white font-semibold">{formatAmount(payment.amount, invoice.currency)}</p>
                  </div>
                  {payment.tds > 0 && (
                    <div>
                      <p className="text-gray-400 text-xs mb-1">TDS</p>
                      <p className="text-yellow-400 font-semibold">{formatAmount(payment.tds, invoice.currency)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Date</p>
                    <p className="text-white">{formatDate(payment.date)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Mode</p>
                    <p className="text-white">{payment.mode}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">Reference</p>
                    <p className="text-white">{payment.reference || "N/A"}</p>
                  </div>
                </div>

                {/* Edit and Delete Buttons */}
                {(onEditPayment || onDeletePayment) && (
                <div className="flex items-center gap-2 ml-4">
                  {onEditPayment && (
                  <button
                    onClick={() => onEditPayment(payment)}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Edit Payment"
                  >
                    <Edit2 size={18} />
                  </button>
                  )}
                  {onDeletePayment && (
                  <button
                    onClick={() => onDeletePayment(payment)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete Payment"
                  >
                    <Trash2 size={18} />
                  </button>
                  )}
                </div>
                )}
              </div>

              {payment.notes && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Notes</p>
                  <p className="text-white text-sm">{payment.notes}</p>
                </div>
              )}
            </div>
          ))}

          {/* Summary */}
          <div className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4 mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-400 text-xs mb-1">Total Amount</p>
                <p className="text-white font-semibold">{formatAmount(invoice.totalAmount, invoice.currency)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Paid Amount</p>
                <p className="text-green-400 font-semibold">
                  {formatAmount(
                    invoice.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0),
                    invoice.currency
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">TDS Deducted</p>
                <p className="text-yellow-400 font-semibold">
                  {formatAmount(
                    invoice.payments.reduce((sum, p) => sum + (parseFloat(p.tds) || 0), 0),
                    invoice.currency
                  )}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Due Amount</p>
                <p className="text-red-400 font-semibold">{formatAmount(invoice.dueAmount || 0, invoice.currency)}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-400">No payments recorded yet</p>
        </div>
      )}
    </div>
  );
};

// Credit Notes Tab Component
export const CreditNotesTab = ({
  invoice,
  onAddCreditNote,
  onEditCreditNote,
  onDeleteCreditNote
}) => {
  return (
    <div className="py-4">
      {/* Add Credit Note Button */}
      {invoice.dueAmount > 0 && onAddCreditNote && (
        <div className="flex justify-end mb-4">
          <button
            onClick={onAddCreditNote}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            +Add Credit Note
          </button>
        </div>
      )}

      {/* Credit Notes List */}
      {invoice.creditNotes && invoice.creditNotes.length > 0 ? (
        <div className="space-y-4">
          {invoice.creditNotes.map((creditNote) => (
            <div
              key={creditNote.id}
              className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[#C99F4A] font-semibold text-lg">
                      {formatAmount(creditNote.amount, invoice.currency)}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {formatDate(creditNote.date)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <span className="text-gray-400 text-sm">Reason: </span>
                      <span className="text-white text-sm">{creditNote.reason}</span>
                    </div>

                    {creditNote.reference && (
                      <div>
                        <span className="text-gray-400 text-sm">Reference: </span>
                        <span className="text-white text-sm">{creditNote.reference}</span>
                      </div>
                    )}

                    {creditNote.notes && (
                      <div>
                        <span className="text-gray-400 text-sm">Notes: </span>
                        <span className="text-white text-sm">{creditNote.notes}</span>
                      </div>
                    )}
                  </div>
                </div>

                {(onEditCreditNote || onDeleteCreditNote) && (
                <div className="flex items-center gap-2">
                  {onEditCreditNote && (
                  <button
                    onClick={() => onEditCreditNote(creditNote)}
                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Edit credit note"
                  >
                    <Edit2 size={16} />
                  </button>
                  )}
                  {onDeleteCreditNote && (
                  <button
                    onClick={() => onDeleteCreditNote(creditNote.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete credit note"
                  >
                    <Trash2 size={16} />
                  </button>
                  )}
                </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-400">No credit notes added yet</p>
          {invoice.dueAmount > 0 && onAddCreditNote && (
            <button
              onClick={onAddCreditNote}
              className="mt-4 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
            >
              Add First Credit Note
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Notes Tab Component
export const NotesTab = ({
  invoice,
  newNoteText,
  setNewNoteText,
  savingNote,
  onAddNote
}) => {
  return (
    <div className="py-4">
      {/* Add Note Section */}
      {onAddNote && setNewNoteText && (
      <div className="mb-6">
        <label className="block text-gray-400 text-sm mb-2">
          Add New Note
        </label>
        <textarea
          value={newNoteText || ""}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder="Enter your note here..."
          rows={4}
          className="w-full px-4 py-3 bg-[#0f0f23] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C99F4A] transition-colors resize-none"
        ></textarea>
        <div className="flex justify-end mt-3">
          <button
            onClick={onAddNote}
            disabled={savingNote || !(newNoteText || "").trim()}
            className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingNote ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
      )}

      {/* Notes List */}
      {invoice.notes && invoice.notes.length > 0 ? (
        <div>
          <h4 className="text-white font-semibold mb-4">Previous Notes</h4>
          <div className="space-y-4">
            {[...invoice.notes].reverse().map((note) => (
              <div
                key={note.id}
                className="bg-[#0f0f23] border border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-400 mb-1">
                      By {note.createdBy} •{" "}
                      {new Date(note.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                </div>
                <p className="text-white whitespace-pre-wrap">{note.text}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 border-t border-gray-700">
          <p className="text-sm text-gray-400">No previous notes</p>
        </div>
      )}
    </div>
  );
};

// Timeline Tab Component
export const TimelineTab = ({
  invoice,
  onViewTimelineEvent
}) => {
  const eventTitles = {
    invoice_created: "Invoice was created",
    contact_updated: "Contact was updated",
    due_date_updated: "Due date was updated",
    payment_captured: "Payment was captured",
    payment_updated: "Payment was updated",
    payment_deleted: "Payment was deleted",
    credit_note_added: "Credit note was added",
    credit_note_updated: "Credit note was updated",
    credit_note_deleted: "Credit note was deleted",
    note_added: "Note was added",
    expenses_attached: "Expenses were attached",
    invoice_updated: "Invoice was updated"
  };

  return (
    <div className="py-4">
      {invoice.timeline && invoice.timeline.length > 0 ? (
        <div className="relative">
          {/* Timeline vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-700"></div>

          {/* Timeline events */}
          <div className="space-y-6">
            {[...invoice.timeline].reverse().map((event, index) => {
              const isFirst = index === 0;
              const eventTitle = eventTitles[event.type] || "Invoice was updated";

              return (
                <div
                  key={event.id}
                  onClick={() => onViewTimelineEvent(event)}
                  className="relative pl-16 pr-4 cursor-pointer hover:bg-[#252540] rounded-lg p-4 transition-colors"
                >
                  {/* Timeline dot */}
                  <div className={`absolute left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isFirst ? 'bg-green-500 border-green-500' : 'bg-[#1a1a2e] border-gray-600'}`}>
                    {isFirst && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>

                  {/* Event badge */}
                  {isFirst && (
                    <div className="absolute left-12 -top-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                      Current
                    </div>
                  )}

                  {/* Event content */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-white font-medium">{eventTitle}</h4>
                      <span className="text-xs text-gray-500">
                        {new Date(event.timestamp).toLocaleString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">
                      By {event.by}
                    </p>
                    {event.changes && Object.keys(event.changes).length > 0 && (
                      <div className="mt-2 text-xs text-blue-400">
                        Click to view changes
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Start indicator */}
          <div className="relative pl-16 pt-4">
            <div className="absolute left-4 w-5 h-5 rounded-full bg-gray-700 border-2 border-gray-600"></div>
            <p className="text-sm text-gray-500">Start</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-sm text-gray-400">No timeline events yet</p>
        </div>
      )}
    </div>
  );
};
